import type * as CookieConsentTypes from 'vanilla-cookieconsent'

import {
  localizePath,
  locales,
  type Locale,
} from '@/lib/i18n/config'
import { getMessages } from '@/lib/i18n/messages'
import { TRACKING } from '@/lib/tracking/config'
import { syncTrackingConsent } from '@/lib/tracking/syncConsent'

import {
  COOKIE_CONSENT_CATEGORY,
  COOKIE_CONSENT_COOKIE_NAME,
  COOKIE_CONSENT_EXPIRES_AFTER_DAYS,
  COOKIE_CONSENT_REVISION,
  COOKIE_CONSENT_SERVICE,
} from './constants'
import {
  lockCookieConsentPageScroll,
  unlockCookieConsentPageScroll,
} from './scrollLock'
import { enhancePreferencesModalKeyboard } from './keyboardAccess'

type ConsentChangeNotifier = () => void

function createRequiredTechnologyRows(locale: Locale) {
  const copy = getMessages(locale).cookieConsent.preferencesModal
  const { storageItems } = copy

  return [
    {
      name: COOKIE_CONSENT_COOKIE_NAME,
      provider: storageItems.consentCookie.provider,
      purpose: storageItems.consentCookie.purpose,
      type: storageItems.consentCookie.type,
      expiration: storageItems.consentCookie.expiration,
    },
    {
      name: 'payload-theme',
      provider: storageItems.themeStorage.provider,
      purpose: storageItems.themeStorage.purpose,
      type: storageItems.themeStorage.type,
      expiration: storageItems.themeStorage.expiration,
    },
    {
      name: 'CARTO basemap',
      provider: storageItems.cartoMaps.provider,
      purpose: storageItems.cartoMaps.purpose,
      type: storageItems.cartoMaps.type,
      expiration: storageItems.cartoMaps.expiration,
    },
    {
      name: 'Virtual tours',
      provider: storageItems.virtualTours.provider,
      purpose: storageItems.virtualTours.purpose,
      type: storageItems.virtualTours.type,
      expiration: storageItems.virtualTours.expiration,
    },
  ]
}

function createAnalyticsCookieRows(locale: Locale) {
  const { analyticsCookies } = getMessages(locale).cookieConsent.preferencesModal
  const provider = 'Google'
  const type = 'First-party cookie'

  return [
    {
      name: '_ga',
      provider,
      purpose: analyticsCookies.ga.purpose,
      type,
      expiration: analyticsCookies.ga.expiration,
    },
    {
      name: `_ga_${TRACKING.gaMeasurementId.replace('G-', '')}`,
      provider,
      purpose: analyticsCookies.gaMeasurement.purpose,
      type,
      expiration: analyticsCookies.gaMeasurement.expiration,
    },
  ]
}

function createMarketingCookieRows(locale: Locale) {
  const { marketingCookies } = getMessages(locale).cookieConsent.preferencesModal
  const googleProvider = 'Google'
  const metaProvider = 'Meta'
  const firstParty = 'First-party cookie'
  const thirdParty = 'Third-party cookie'

  return [
    {
      name: '_gcl_au',
      provider: googleProvider,
      purpose: marketingCookies.gclAu.purpose,
      type: firstParty,
      expiration: marketingCookies.gclAu.expiration,
    },
    {
      name: '_gcl_aw',
      provider: googleProvider,
      purpose: marketingCookies.gclAw.purpose,
      type: firstParty,
      expiration: marketingCookies.gclAw.expiration,
    },
    {
      name: '_gcl_gb',
      provider: googleProvider,
      purpose: marketingCookies.gclGb.purpose,
      type: firstParty,
      expiration: marketingCookies.gclGb.expiration,
    },
    {
      name: 'IDE',
      provider: googleProvider,
      purpose: marketingCookies.ide.purpose,
      type: thirdParty,
      expiration: marketingCookies.ide.expiration,
    },
    {
      name: '_fbp',
      provider: metaProvider,
      purpose: marketingCookies.fbp.purpose,
      type: firstParty,
      expiration: marketingCookies.fbp.expiration,
    },
    {
      name: '_fbc',
      provider: metaProvider,
      purpose: marketingCookies.fbc.purpose,
      type: firstParty,
      expiration: marketingCookies.fbc.expiration,
    },
  ]
}

function createExternalServiceCookieRows(locale: Locale) {
  const { chatbotStorage, salesforceCookies } =
    getMessages(locale).cookieConsent.preferencesModal
  const salesforceProvider = 'Salesforce / Cresco Capital'
  const chatbotProvider = 'Crescore / Telefonagent'
  const cookieType = 'Third-party cookie'

  return [
    {
      name: 'BrowserId',
      provider: salesforceProvider,
      purpose: salesforceCookies.browserId.purpose,
      type: cookieType,
      expiration: salesforceCookies.browserId.expiration,
    },
    {
      name: 'CookieConsentPolicy',
      provider: salesforceProvider,
      purpose: salesforceCookies.cookieConsentPolicy.purpose,
      type: cookieType,
      expiration: salesforceCookies.cookieConsentPolicy.expiration,
    },
    {
      name: 'LSKey-c$CookieConsentPolicy',
      provider: salesforceProvider,
      purpose: salesforceCookies.lockerConsentPolicy.purpose,
      type: cookieType,
      expiration: salesforceCookies.lockerConsentPolicy.expiration,
    },
    {
      name: 'apex__BookingPage___PAGE_TARGET',
      provider: salesforceProvider,
      purpose: salesforceCookies.bookingPageTarget.purpose,
      type: cookieType,
      expiration: salesforceCookies.bookingPageTarget.expiration,
    },
    {
      name: 'apex__BookingProcess___lang',
      provider: salesforceProvider,
      purpose: salesforceCookies.bookingLanguage.purpose,
      type: cookieType,
      expiration: salesforceCookies.bookingLanguage.expiration,
    },
    {
      name: 'chatbot-widget-9yaj.vercel.app',
      provider: chatbotProvider,
      purpose: chatbotStorage.widgetHost.purpose,
      type: chatbotStorage.widgetHost.type,
      expiration: chatbotStorage.widgetHost.expiration,
    },
    {
      name: 'crescore_session_id',
      provider: chatbotProvider,
      purpose: chatbotStorage.sessionId.purpose,
      type: chatbotStorage.sessionId.type,
      expiration: chatbotStorage.sessionId.expiration,
    },
    {
      name: 'Supabase',
      provider: chatbotStorage.supabase.provider,
      purpose: chatbotStorage.supabase.purpose,
      type: chatbotStorage.supabase.type,
      expiration: chatbotStorage.supabase.expiration,
    },
    {
      name: 'Telefonagent',
      provider: chatbotStorage.telefonagent.provider,
      purpose: chatbotStorage.telefonagent.purpose,
      type: chatbotStorage.telefonagent.type,
      expiration: chatbotStorage.telefonagent.expiration,
    },
  ]
}

export function createCookieConsentTranslation(
  locale: Locale,
): CookieConsentTypes.Translation {
  const copy = getMessages(locale).cookieConsent
  const preferences = copy.preferencesModal
  const privacyPolicyHref = localizePath('/privacy-policy', locale)
  const privacyPolicyLink = `<a class="cc__link" href="${privacyPolicyHref}">${preferences.privacyPolicyLabel}</a>`
  const consentDescription = copy.consentModal.description
    .replaceAll('{{privacyPolicyLink}}', privacyPolicyLink)
    .replaceAll('\n\n', '<br><br>')
    .replaceAll('\n', '<br>')

  return {
    consentModal: {
      label: copy.consentModal.label,
      title: copy.consentModal.title,
      description: `${consentDescription}<br>{{revisionMessage}}`,
      acceptAllBtn: copy.consentModal.acceptAllBtn,
      acceptNecessaryBtn: copy.consentModal.acceptNecessaryBtn,
      showPreferencesBtn: copy.consentModal.showPreferencesBtn,
      revisionMessage: copy.consentModal.revisionMessage,
      footer: privacyPolicyLink,
    },
    preferencesModal: {
      title: preferences.title,
      acceptAllBtn: preferences.acceptAllBtn,
      acceptNecessaryBtn: preferences.acceptNecessaryBtn,
      savePreferencesBtn: preferences.savePreferencesBtn,
      closeIconLabel: preferences.closeIconLabel,
      serviceCounterLabel: preferences.serviceCounterLabel,
      sections: [
        {
          title: preferences.introTitle,
          description: preferences.introDescription,
        },
        {
          title: preferences.necessaryTitle,
          description: preferences.necessaryDescription,
          linkedCategory: COOKIE_CONSENT_CATEGORY.necessary,
          cookieTable: {
            caption: preferences.storageTableCaption,
            headers: preferences.tableHeaders,
            body: createRequiredTechnologyRows(locale),
          },
        },
        {
          title: preferences.analyticsTitle,
          description: preferences.analyticsDescription,
          linkedCategory: COOKIE_CONSENT_CATEGORY.analytics,
          cookieTable: {
            caption: preferences.analyticsTableCaption,
            headers: preferences.tableHeaders,
            body: createAnalyticsCookieRows(locale),
          },
        },
        {
          title: preferences.marketingTitle,
          description: preferences.marketingDescription,
          linkedCategory: COOKIE_CONSENT_CATEGORY.marketing,
          cookieTable: {
            caption: preferences.marketingTableCaption,
            headers: preferences.tableHeaders,
            body: createMarketingCookieRows(locale),
          },
        },
        {
          title: preferences.externalServicesTitle,
          description: preferences.externalServicesDescription,
          linkedCategory: COOKIE_CONSENT_CATEGORY.externalServices,
          cookieTable: {
            caption: preferences.salesforceTableCaption,
            headers: preferences.tableHeaders,
            body: createExternalServiceCookieRows(locale),
          },
        },
        {
          title: preferences.moreInformationTitle,
          description: `${preferences.moreInformationDescription} ${privacyPolicyLink}`,
        },
      ],
    },
  }
}

export function createCookieConsentConfig(
  initialLocale: Locale,
  notifyConsentChange: ConsentChangeNotifier = () => undefined,
): CookieConsentTypes.CookieConsentConfig {
  const translations = Object.fromEntries(
    locales.map((locale) => [locale, createCookieConsentTranslation(locale)]),
  ) as Record<Locale, CookieConsentTypes.Translation>

  const handleConsentLifecycle = () => {
    notifyConsentChange()
    syncTrackingConsent()
  }

  return {
    mode: 'opt-in',
    revision: COOKIE_CONSENT_REVISION,
    manageScriptTags: true,
    autoClearCookies: true,
    disablePageInteraction: true,
    hideFromBots: process.env.NODE_ENV === 'production',
    cookie: {
      name: COOKIE_CONSENT_COOKIE_NAME,
      expiresAfterDays: COOKIE_CONSENT_EXPIRES_AFTER_DAYS,
      path: '/',
      sameSite: 'Lax',
      secure: true,
    },
    guiOptions: {
      consentModal: {
        layout: 'box wide',
        position: 'middle center',
        // Outline "Use necessary only" to match Save/Manage preferences.
        equalWeightButtons: false,
        flipButtons: false,
      },
      preferencesModal: {
        layout: 'box',
        equalWeightButtons: false,
        flipButtons: false,
      },
    },
    categories: {
      [COOKIE_CONSENT_CATEGORY.necessary]: {
        enabled: true,
        readOnly: true,
      },
      [COOKIE_CONSENT_CATEGORY.analytics]: {
        enabled: false,
        autoClear: {
          cookies: [{ name: /^_ga/ }, { name: '_gid' }],
        },
        services: {
          [COOKIE_CONSENT_SERVICE.googleAnalytics]: {
            label: 'Google Analytics 4',
            onAccept: handleConsentLifecycle,
            onReject: handleConsentLifecycle,
          },
        },
      },
      [COOKIE_CONSENT_CATEGORY.marketing]: {
        enabled: false,
        autoClear: {
          cookies: [
            { name: '_gcl_au' },
            { name: '_gcl_aw' },
            { name: '_gcl_gb' },
            { name: '_fbp' },
            { name: '_fbc' },
          ],
        },
        services: {
          [COOKIE_CONSENT_SERVICE.googleAds]: {
            label: 'Google Ads',
            cookies: [{ name: '_gcl_au' }, { name: '_gcl_aw' }, { name: '_gcl_gb' }],
            onAccept: handleConsentLifecycle,
            onReject: handleConsentLifecycle,
          },
          [COOKIE_CONSENT_SERVICE.metaPixel]: {
            label: 'Meta Pixel',
            cookies: [{ name: '_fbp' }, { name: '_fbc' }],
            onAccept: handleConsentLifecycle,
            onReject: handleConsentLifecycle,
          },
        },
      },
      [COOKIE_CONSENT_CATEGORY.externalServices]: {
        enabled: false,
        services: {
          [COOKIE_CONSENT_SERVICE.salesforceBooking]: {
            label: 'Salesforce / Cresco booking',
            onAccept: notifyConsentChange,
            onReject: notifyConsentChange,
          },
          [COOKIE_CONSENT_SERVICE.crescoreChatbot]: {
            label: 'Crescore chat',
            onAccept: notifyConsentChange,
            onReject: notifyConsentChange,
          },
        },
      },
    },
    language: {
      default: initialLocale,
      translations,
    },
    onFirstConsent: handleConsentLifecycle,
    onConsent: handleConsentLifecycle,
    onChange: handleConsentLifecycle,
    onModalShow: () => {
      lockCookieConsentPageScroll()
    },
    onModalHide: () => {
      unlockCookieConsentPageScroll()
    },
    onModalReady: ({ modalName, modal }) => {
      const scrollTarget =
        modalName === 'preferencesModal'
          ? modal.querySelector('.pm__body')
          : modalName === 'consentModal'
            ? modal.querySelector('.cm__desc')
            : null

      scrollTarget?.setAttribute('data-lenis-prevent', '')

      if (modalName === 'preferencesModal') {
        enhancePreferencesModalKeyboard(modal)
      }
    },
  }
}
